import React, { useState, useEffect, useMemo } from 'react';
import { handlePackageInputChange } from "../../redux/slices/billSlice";

const PackageCard = ({ data, state, dispatch, onClose, handleUpdateServices }) => {
  const [selectedServices, setSelectedServices] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [serviceSelections, setServiceSelections] = useState({});
  
  // Filter valid packages (with services and valid dates)
const validPackages = useMemo(() => {
  return (Array.isArray(data) ? data : [data]).filter(pkg => {
    if (!pkg || !pkg.services || pkg.services.length === 0) return false;
    if (!pkg.validity) return false;
    const validityDate = new Date(pkg.validity);
    return !isNaN(validityDate.getTime()) && validityDate > new Date();
  });
}, [data]);

  // Initialize selected services
  useEffect(() => {
  const servicesInBill = new Set(state.services.map(s => s.service));
  const initialSelections = {};
  let allSelected = [];

  validPackages.forEach((pkg, tabIndex) => {
    initialSelections[tabIndex] = pkg.services
      .filter(service => servicesInBill.has(service._id))
      .map(service => service._id);

    pkg.services.forEach(service => {
      if (servicesInBill.has(service._id) && !allSelected.some(s => s._id === service._id)) {
        allSelected.push(service);
      }
    });
  });

  setServiceSelections({});
  setSelectedServices([]);
}, [validPackages, state.services]);


  const handleCheckboxChange = (service, tabIndex) => {
    console.log(service,'service')
    console.log(tabIndex)
    const isSelected = serviceSelections[tabIndex]?.includes(service._id) || false;

    setServiceSelections(prev => {
      const newSelections = { ...prev };
      if (!newSelections[tabIndex]) newSelections[tabIndex] = [];

      if (isSelected) {
        newSelections[tabIndex] = newSelections[tabIndex].filter(id => id !== service._id);
      } else {
        newSelections[tabIndex] = [...newSelections[tabIndex], service._id];
      }
      return newSelections;
    });

    setSelectedServices(prev => {
      if (isSelected) {
        return prev.filter(item => item._id !== service._id);
      }
      return prev.some(item => item._id === service._id) 
        ? prev 
        : [...prev, service];
    });
  };

  const applyHandler = () => {
    const servicePackageMap = {};
    validPackages.forEach(pkg => {
      pkg.services?.forEach(service => {
        servicePackageMap[service._id] = pkg.uniqueId;
      });
    });

    const updatedServices = selectedServices.map(service => ({
      serviceName: service.serviceName,
      service: service._id,
      category: service.category,
      quantity: 1,
      serviceProvider: [],
      price: service.price,
      discount: 100,
      discountType: '%',
      amount: 0,
      packageApplied: true,
      packageUniqueId: servicePackageMap[service._id]
    }));

    const allPackageUniqueIds = [...new Set(
      selectedServices.map(service => servicePackageMap[service._id])
    )];

    dispatch(handlePackageInputChange({
      usePackage: true,
      item: validPackages[activeTab],
      selectedServices: updatedServices,
      packageUniqueId: allPackageUniqueIds
    }));

    handleUpdateServices(updatedServices);
    onClose();
  };

  const removeHandler = () => {
    dispatch(handlePackageInputChange({
      usePackage: false,
      item: null,
      selectedServices: []
    }));
    onClose();
  };

  const getSelectedCountForTab = (tabIndex) => {
    return serviceSelections[tabIndex]?.length || 0;
  };

  const activePackage = validPackages[activeTab];

  if (validPackages.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>Package Card</h2>
        </div>
        <div style={styles.errorMessage}>
          No valid packages available.
        </div>
        <div style={styles.actionContainer}>
          <button style={styles.removeButton} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Package Card</h2>
      </div>

      <div style={styles.membershipContent}>
        {validPackages.length > 1 && (
          <div style={styles.tabContainer}>
            {validPackages.map((pkg, index) => (
              <button
                key={pkg._id}
                style={{
                  ...styles.tabButton,
                  ...(activeTab === index && styles.activeTab)
                }}
                onClick={() => setActiveTab(index)}
              >
                {`package ${index + 1}`}
                {getSelectedCountForTab(index) > 0 && (
                  <span style={styles.badge}>
                    {getSelectedCountForTab(index)}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        <div style={styles.detailsContainer}>
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Package:</span>
            <span>{activePackage.packageName}</span>
          </div>
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Price:</span>
            <span>₹{activePackage.price}</span>
          </div>
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Valid Until:</span>
            <span>
              {new Date(activePackage.validity).toLocaleDateString('en-GB')}
            </span>
          </div>

          <div style={styles.servicesContainer}>
            <div style={styles.tableHeader}>
              <div style={{ ...styles.tableCell, flex: 0.5 }}>Select</div>
              <div style={styles.tableCell}>Service</div>
              <div style={styles.tableCell}>Category</div>
              <div style={styles.tableCell}>Price</div>
            </div>
            <div style={styles.servicesTable}>
              {activePackage.services.map(service => (
                <div
                  key={service._id}
                  style={{
                    ...styles.tableRow,
                    ...(serviceSelections[activeTab]?.includes(service._id) && styles.selectedRow)
                  }}
                >
                  <div style={{ ...styles.tableCell, flex: 0.5 }}>
                    <input
                      type="checkbox"
                      checked={serviceSelections[activeTab]?.includes(service._id) || false}
                      onChange={() => handleCheckboxChange(service, activeTab)}
                      style={styles.checkbox}
                    />
                  </div>
                  <div style={styles.tableCell}>{service.serviceName}</div>
                  <div style={styles.tableCell}>{service.category}</div>
                  <div style={styles.tableCell}>₹{service.price}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={styles.actionContainer}>
          {selectedServices.length > 0 ? (
            <>
              <button style={styles.applyButton} onClick={applyHandler}>
                Apply {selectedServices.length} Service{selectedServices.length !== 1 ? 's' : ''}
              </button>
              {state.usePackage && (
                <button style={styles.removeButton} onClick={removeHandler}>
                  Remove Package
                </button>
              )}
            </>
          ) : (
            <div style={styles.errorMessage}>
              Please select at least one service to apply
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
// Reuse the same styles object from MembershipCard
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '500px',
    maxWidth: '90vw',
    // minHeight: '400px',
    minHeight: '600px',
    padding: '20px',
    borderRadius: '10px',
    backgroundColor: '#fff',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '10px',
    borderBottom: '1px solid #eee',
    position: 'relative',
  },
  title: {
    margin: 0,
    color: '#333',
    fontSize: '1.5rem',
  },
  membershipContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  },
  tabContainer: {
    display: 'flex',
    gap: '5px',
    marginBottom: '15px',
    overflowX: 'auto',
    padding: '5px 0',
  },
  tabButton: {
    padding: '8px 15px',
    borderRadius: '5px',
    border: '1px solid #ddd',
    backgroundColor: '#f5f5f5',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontSize: '14px',
    position: 'relative',
  },
  activeTab: {
    backgroundColor: '#6174DD',
    color: 'white',
    borderColor: '#6174DD',
  },
  badge: {
    position: 'absolute',
    top: '-8px',
    right: '-8px',
    backgroundColor: '#ff4444',
    color: 'white',
    borderRadius: '50%',
    width: '18px',
    height: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
  },
  detailsContainer: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  servicesContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
    marginTop: '15px',
    border: '1px solid #eee',
    borderRadius: '8px',
  },
  tableHeader: {
    display: 'flex',
    backgroundColor: '#6174DD',
    color: 'white',
    padding: '12px 10px',
    fontWeight: '600',
  },
  servicesTable: {
    flex: 1,
    overflowY: 'auto',
  },
  tableRow: {
    display: 'flex',
    padding: '12px 10px',
    borderBottom: '1px solid #eee',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  selectedRow: {
    backgroundColor: '#f0f0ff',
  },
  disabledRow: {
    backgroundColor: '#f9f9f9',
    color: '#999',
    cursor: 'not-allowed',
  },
  tableCell: {
    flex: 1,
    padding: '0 5px',
    display: 'flex',
    alignItems: 'center',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
    margin: '0 auto',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 0',
    borderBottom: '1px solid #f0f0f0',
  },
  detailLabel: {
    fontWeight: '600',
    color: '#555',
  },
  actionContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '10px',
    paddingTop: '15px',
    marginTop: 'auto',
  },
  applyButton: {
    padding: '10px 20px',
    backgroundColor: '#6174DD',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#4a5bc7',
    },
  },
  removeButton: {
    padding: '10px 20px',
    backgroundColor: '#ff4444',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#cc0000',
    },
  },
  errorMessage: {
    color: 'red',
    marginRight: 'auto',
    fontSize: '14px',
  },
  expiredText: {
    color: 'red',
    fontWeight: '600',
  },
};

export default PackageCard;
